import compare from 'just-compare';
import { isText } from 'lib-knowledge-document-structure/plate/common';
import {
  ELEMENT_PARAGRAPH,
  MyRootBlock,
  MyValue,
} from 'lib-knowledge-document-structure/plateTypes';
import { DeepReadonly } from 'ts-essentials';
import { proxy } from 'valtio';

import { ArticleTranslationDetails } from './savingStore';

/**
 * Checks if the passed in body is empty. Both the min slate doc or []. see
 * `getEmptyValue` from './hooks/useEditor'
 */
export function isBodyEmpty(
  bodyValue: MyValue | DeepReadonly<MyValue>,
): boolean {
  if (bodyValue.length === 0) return true;

  // Check to see if we are the emptyDocument
  if (bodyValue.length === 1) {
    // I know it's got to be an element not text.
    const element = bodyValue[0];
    if (
      element.type === ELEMENT_PARAGRAPH &&
      element.children.length === 1 &&
      isText(element.children[0]) &&
      element.children[0].text.length === 0 &&
      // If we have extra marks then the document has been modified.
      Object.keys(element.children[0]).length === 1
    )
      return true;
  }

  return false;
}

/** Checks if the old and new title are different */
export const hasTitleChanged = (oldValue: string, newValue: string): boolean =>
  oldValue !== newValue;

export const online = proxy<{ isOnline: boolean }>({
  isOnline: true,
});

(function watchOnline() {
  const onlineCallback = () => (online.isOnline = true);
  const offlineCallback = () => (online.isOnline = false);
  window.addEventListener('offline', offlineCallback);
  window.addEventListener('online', onlineCallback);

  // Initial value is set.
  online.isOnline = navigator.onLine;
})();

/**
 * This method returns the newValue by adding modifiedAt for the nodes that have
 * been modified. It also returns if the article has changed
 */
export function trackChanges(
  oldValue: DeepReadonly<ArticleTranslationDetails> | ArticleTranslationDetails,
  newValue: DeepReadonly<ArticleTranslationDetails> | ArticleTranslationDetails,
): { article: ArticleTranslationDetails; hasChanges: boolean } {
  if (isBodyEmpty(oldValue.body) && isBodyEmpty(newValue.body)) {
    return {
      article: newValue as ArticleTranslationDetails,
      hasChanges: false,
    };
  }
  let hasBodyChanged = newValue.body.length != oldValue.body.length;
  const body: MyRootBlock[] = newValue.body.map((child) => {
    const oldChild = oldValue.body.find((c) => c.id === child.id);
    if (oldChild && compare(child, oldChild)) {
      return child as MyRootBlock;
    }
    hasBodyChanged = true;
    return {
      ...child,
      updatedAt: new Date().toISOString(),
    } as MyRootBlock;
  });
  const titleChanged = hasTitleChanged(oldValue.title, newValue.title);
  return {
    article: {
      ...newValue,
      body,
      ...(titleChanged ? { titleUpdatedAt: new Date().toISOString() } : {}),
    },
    hasChanges: hasBodyChanged || titleChanged,
  };
}
